import type { ReactNode } from 'react';

/**
 * apps/customer/src/components/legal/TermsOfServiceContent.tsx
 *
 * SIL-1509: the public Terms of Service body served at /terms, including the
 * SMS Terms section US toll-free SMS registration requires. Static content
 * only, mirrored from docs/legal/terms-of-service.md - no markdown renderer
 * is a dependency of this app, so any edit needs both files updated.
 *
 * The body is the platform user agreement draft (docs/legal/
 * user-agreement-platform.md) with every unresolved placeholder / attorney
 * decision removed or replaced by neutral wording. Do NOT reintroduce
 * bracketed placeholders here; see docs/legal/open-decisions.md.
 *
 * No auth, no data fetching: safe to render from an unauthenticated route.
 * This is separate from /tos, the authenticated clickwrap acceptance screen.
 */

type Block =
  | { kind: 'p'; text: ReactNode }
  | { kind: 'h3'; text: string }
  | { kind: 'ul'; items: ReactNode[] };

interface Section {
  heading: string;
  blocks: Block[];
}

const SUPPORT_EMAIL = 'support@siliconx.com';

const supportLink = (
  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-brand-600 underline">
    {SUPPORT_EMAIL}
  </a>
);

const privacyLink = (
  <a href="/privacy" className="text-brand-600 underline">
    Privacy Policy
  </a>
);

const INTRO_BLOCKS: Block[] = [
  {
    kind: 'p',
    text: 'These Terms are the baseline terms for general use of the SiliconX customer portal: signing in, organization and team administration, browsing your asset inventory, and viewing FMV estimates, forecasts, and reports. Digital Passport generation, the TrueRead probe, and the exchange/marketplace (listings, bids, offers, and sales) are covered by separate terms, which apply in addition to these Terms when you use those features and are presented to you before you do.',
  },
];

const SECTIONS: Section[] = [
  {
    heading: '1. About These Terms',
    blocks: [
      {
        kind: 'p',
        text: 'These Terms of Service (these "Terms") are between Silicon Exchange, Inc. ("Silicon Exchange," "SiliconX," "we," "us," or "our") and the person or organization that creates or uses an account to access the SiliconX platform ("you").',
      },
      {
        kind: 'p',
        text: 'By creating an account, clicking to accept these Terms, or otherwise using the Services, you agree to these Terms. If you do not agree, do not use the Services. "Services" means the SiliconX customer portal and related platform features covered by these Terms, excluding Digital Passport generation and the Exchange, which are governed by their own terms when you use those features.',
      },
    ],
  },
  {
    heading: '2. Eligibility',
    blocks: [
      {
        kind: 'p',
        text: 'The Services are intended for business and professional use by adults at least 18 years old who are able to form a binding contract. You may not use the Services if you are barred from doing so under applicable law, including because you are listed on, or owned or controlled by a person listed on, a government sanctions or denied-party list.',
      },
    ],
  },
  {
    heading: '3. Accounts, Organizations, and Team Roles',
    blocks: [
      { kind: 'h3', text: '3.1 Accounts and Authority' },
      {
        kind: 'p',
        text: 'Accounts are created by invitation or a referral code SiliconX issues. If you create an account or accept these Terms on behalf of an organization, you represent that you are authorized to bind that organization, and the organization is responsible for all activity under its account.',
      },
      { kind: 'h3', text: '3.2 Organizations and Team Roles' },
      {
        kind: 'p',
        text: "The Services support organization-level accounts with multiple individual users assigned roles that govern what each user can see and do within the organization's account. An organization's administrators are responsible for assigning roles appropriately and for all activity by users they add, including after that user's employment or engagement with the organization ends, until the organization removes that user's access.",
      },
      { kind: 'h3', text: '3.3 Credentials' },
      {
        kind: 'p',
        text: (
          <>
            Accounts use email-based sign-in. You must keep your credentials confidential, must not
            share them, and must notify us promptly at {supportLink} if you suspect unauthorized
            access. You are responsible for activity under your credentials until you notify us and
            we have had a reasonable opportunity to act.
          </>
        ),
      },
      { kind: 'h3', text: '3.4 Accuracy of Account Information' },
      {
        kind: 'p',
        text: "You must keep your account and organization information accurate and complete. We may ask you to verify your identity, your organization's legal identity, or your authority, and may limit or suspend features until you do.",
      },
    ],
  },
  {
    heading: '4. Acceptable Use',
    blocks: [
      {
        kind: 'p',
        text: 'You may use the Services only for lawful purposes and in accordance with these Terms. You must not, and must not permit anyone else to:',
      },
      {
        kind: 'ul',
        items: [
          'violate any law or regulation, or infringe or misappropriate the rights of any person;',
          'provide false, misleading, or fraudulent information, or impersonate any person or organization;',
          'access an account, asset record, or data you are not authorized to access, or attempt to bypass an access control, role restriction, or organization boundary;',
          'probe, scan, or interfere with the Services except through a security program we have authorized in writing;',
          'use automated means to extract data from the Services beyond the features we make available to you;',
          'reverse engineer, decompile, or attempt to derive the source code or non-public methods of the Services, including the FMV estimation and reporting tools; or',
          'rely on an FMV estimate, forecast, or report in a manner inconsistent with Section 6 below.',
        ],
      },
      {
        kind: 'p',
        text: 'We may investigate suspected violations, remove content, restrict features, and cooperate with law enforcement and regulators.',
      },
    ],
  },
  {
    heading: '5. Your Content and Licence to SiliconX',
    blocks: [
      { kind: 'h3', text: '5.1 Ownership' },
      {
        kind: 'p',
        text: 'You retain ownership of the information, files, and data you or your users submit to the Services in the ordinary course of platform use, including asset inventory records, notes, and uploaded documents ("Your Content"). As between you and SiliconX, you retain ownership of Your Content. This section does not cover Digital Passport data, which is addressed in the separate terms for that feature.',
      },
      { kind: 'h3', text: '5.2 Licence to SiliconX' },
      {
        kind: 'p',
        text: 'You grant SiliconX a worldwide, non-exclusive, royalty-free licence, for the duration of these Terms and as long afterward as needed for the purposes below, to host, store, reproduce, process, analyze, and display Your Content, and to create derivative works from it, in order to: (a) provide, maintain, and secure the Services, including asset inventory and reporting features; (b) generate FMV estimates, forecasts, and reports for you; (c) comply with law and enforce these Terms; and (d) create Aggregated Data as described in Section 5.3.',
      },
      { kind: 'h3', text: '5.3 Aggregated and De-identified Data' },
      {
        kind: 'p',
        text: 'SiliconX may create and use statistical, aggregated, or de-identified data derived from Your Content ("Aggregated Data"), including to build and improve FMV estimation and forecasting models. Aggregated Data will not identify you, your organization, or a specific asset as yours. SiliconX owns Aggregated Data.',
      },
      { kind: 'h3', text: '5.4 Accuracy and Consent' },
      {
        kind: 'p',
        text: 'You represent and warrant that you have all rights necessary to submit Your Content and grant the licence above, and that Your Content is accurate to the best of your knowledge.',
      },
    ],
  },
  {
    heading: '6. FMV Estimates, Forecasts, and Reports Are Informational Only',
    blocks: [
      {
        kind: 'p',
        text: 'The Services may show fair market value (FMV) estimates, forecasts, price trends, and related reports for hardware assets, generated from market data, comparable transactions, and modeling that SiliconX develops and updates from time to time.',
      },
      {
        kind: 'p',
        text: 'You agree and acknowledge that an FMV estimate, forecast, or report:',
      },
      {
        kind: 'ul',
        items: [
          "is a statistical estimate, not an appraisal, valuation, certification, warranty, or guarantee of any asset's actual market value, condition, remaining life, or resale price;",
          'is based on data and models that may be incomplete, delayed, or wrong, and that SiliconX does not independently verify every input;',
          'may differ materially from the price an asset actually achieves in a transaction; and',
          'is not a substitute for your own diligence, appraisal, or professional advice before a financial, accounting, insurance, tax, or transactional decision.',
        ],
      },
    ],
  },
  {
    heading: '7. Intellectual Property',
    blocks: [
      {
        kind: 'p',
        text: 'SiliconX and its licensors own all right, title, and interest in the Services, including the FMV estimation and reporting tools, the customer portal, and all related technology, excluding Your Content and other content you submit. No rights are granted to you except the limited right to access and use the Services as permitted by these Terms. All trademarks, logos, and the SiliconX name are the property of SiliconX.',
      },
    ],
  },
  {
    heading: '8. Third-Party Services',
    blocks: [
      {
        kind: 'p',
        text: 'The Services may rely on third-party infrastructure and services (for example, cloud hosting, identity providers, SMS delivery providers, and market-data sources). SiliconX is not responsible for the availability or performance of third-party services outside its control.',
      },
    ],
  },
  {
    heading: '9. Privacy',
    blocks: [
      {
        kind: 'p',
        text: (
          <>
            Our collection and use of personal information in connection with the Services is
            described in our {privacyLink}.
          </>
        ),
      },
    ],
  },
  {
    heading: '10. Disclaimers and Limitation of Liability',
    blocks: [
      { kind: 'h3', text: '10.1 Disclaimers' },
      {
        kind: 'p',
        text: 'THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, SILICONX DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. SILICONX DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY FMV ESTIMATE, FORECAST, OR REPORT IS ACCURATE OR COMPLETE.',
      },
      { kind: 'h3', text: '10.2 Limitation of Liability' },
      {
        kind: 'p',
        text: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL SILICONX BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS OR LOST DATA.',
      },
    ],
  },
  {
    heading: '11. Suspension and Termination',
    blocks: [
      {
        kind: 'p',
        text: 'We may suspend or terminate your access to the Services, or a specific account, if you violate these Terms, if required by law, or on notice for convenience after 30 days. You may stop using the Services at any time. Termination of your access under these Terms also ends your ability to use the Digital Passport and Exchange features, since both require an active account. Sections that by their nature should survive termination (including Sections 6, 7, and 10) survive.',
      },
    ],
  },
  {
    heading: '12. Governing Law',
    blocks: [
      {
        kind: 'p',
        text: 'These Terms are governed by the laws of the State of Delaware, without regard to its conflict-of-laws principles.',
      },
    ],
  },
  {
    heading: '13. Changes to These Terms',
    blocks: [
      {
        kind: 'p',
        text: 'We may update these Terms from time to time. We will provide notice of material changes as described in the Services, and continued use after the change takes effect constitutes acceptance. Material changes take effect 30 days after notice.',
      },
    ],
  },
  {
    heading: '14. General',
    blocks: [
      {
        kind: 'p',
        text: 'These Terms, together with the separate terms for Digital Passport and Exchange features (to the extent you use those features) and our Privacy Policy, are the entire agreement between you and SiliconX regarding general platform access, and supersede prior discussions on that subject. If any provision is found unenforceable, the remaining provisions remain in effect. These Terms are not assignable by you without our consent. No waiver of any provision is effective unless in writing.',
      },
    ],
  },
  {
    heading: '15. Contact',
    blocks: [
      {
        kind: 'p',
        text: <>Questions about these Terms or security concerns: {supportLink}.</>,
      },
    ],
  },
  {
    heading: '16. SMS Terms',
    blocks: [
      {
        kind: 'ul',
        items: [
          <>
            <strong>Program name:</strong> SiliconX verification codes.
          </>,
          <>
            <strong>What we send:</strong> one-time verification codes and related account notices,
            by SMS, to the mobile number you enter for buyer verification. We send messages only
            after you enter your number and request a code.
          </>,
          <>
            <strong>Message frequency:</strong> varies based on your actions.
          </>,
          <>
            <strong>Cost:</strong> message and data rates may apply.
          </>,
          <>
            <strong>Opt out and help:</strong> reply STOP to opt out at any time, or HELP for help.
            You can also contact us at {supportLink}.
          </>,
          <>
            <strong>Carriers:</strong> wireless carriers are not liable for delayed or undelivered
            messages.
          </>,
          <>
            <strong>Privacy:</strong> see our {privacyLink} for how we handle your mobile number.
            Mobile numbers and SMS opt-in consent are not sold, rented, or shared with third
            parties or affiliates for marketing or promotional purposes.
          </>,
        ],
      },
    ],
  },
];

function renderBlock(block: Block, i: number) {
  switch (block.kind) {
    case 'p':
      return (
        <p key={i} className="mt-2">
          {block.text}
        </p>
      );
    case 'h3':
      return (
        <h3 key={i} className="mt-3 text-sm font-semibold text-foreground">
          {block.text}
        </h3>
      );
    case 'ul':
      return (
        <ul key={i} className="ml-5 mt-2 list-disc space-y-1">
          {block.items.map((item, j) => (
            <li key={j}>{item}</li>
          ))}
        </ul>
      );
  }
}

export function TermsOfServiceContent() {
  return (
    <>
      <section>
        <h2 className="text-base font-semibold text-foreground">Scope of These Terms</h2>
        {INTRO_BLOCKS.map(renderBlock)}
      </section>
      {SECTIONS.map((section) => (
        <section key={section.heading} id={section.heading.startsWith('16.') ? 'sms-terms' : undefined}>
          <h2 className="text-base font-semibold text-foreground">{section.heading}</h2>
          {section.blocks.map(renderBlock)}
        </section>
      ))}
    </>
  );
}
